// Grille mensuelle — paie arbitration catalog (Phase F).
//
// Payroll DEFAULT (Lot 9 — plus une règle verrouillée): the paie version
// caps every worked day at the expected day-hours automatically (réelle
// keeps the true hours — the truth is never lost), so long days NEVER
// queue for arbitration. Only short days + data anomalies need a human,
// and every classification counts the day at full expected hours (or,
// for « Saisir l'horaire », fixes the punches so the cell recomputes).
// The single classification that pays nothing is « Absence
// injustifiée », where the work shown is itself zero.
//
// Ce défaut se contourne par un choix humain explicite, jamais en
// silence: « Compter les heures réelles » sur une journée (option H
// ci-dessous), ou le flag de fiche « Heures réelles en paie » pour les
// profils horaires / à temps partiel (leurs journées courtes ne font
// alors plus la queue du tout). Aucune option ne calcule de l'argent:
// « Total h paie » est un document de contrôle — le module Salaires paie
// un montant mensuel et ne lit jamais ces heures.
import { ABSENCE_INJUSTIFIEE_CODE } from './statuses.js';
export const GM_KIND_MENUS = {
    court: {
        title: 'Journée courte',
        options: [
            { key: 'J', label: 'Sortie justifiée', desc: 'comptabilisée plein', tone: 'ok', result: 'p' },
            { key: 'R', label: 'Récupération', desc: 'imputée au compteur récup', tone: 'info', result: 'recup' },
            { key: 'D', label: '½ congé', desc: 'demi-journée posée', tone: 'shf', result: 'c' },
            { key: 'M', label: 'Maladie', desc: 'sous réserve du justificatif', tone: 'info', result: 'm' },
            { key: 'C', label: 'Congé', desc: 'journée posée sur le solde', tone: 'shf', result: 'c' },
            // Le seul choix qui compte la journée sous les heures attendues —
            // et c'est un choix, posé à la main, cellule par cellule.
            { key: 'H', label: 'Compter les heures réelles', desc: 'les heures pointées, telles quelles', tone: 'warn', result: 'reel' },
        ],
    },
    absj: {
        title: 'Absence à requalifier',
        options: [
            { key: 'M', label: 'Maladie', desc: 'sous réserve du justificatif', tone: 'info', result: 'm' },
            { key: 'C', label: 'Congé', desc: 'journée posée sur le solde', tone: 'shf', result: 'c' },
            { key: 'R', label: 'Récupération', desc: '−1 j sur récupération', tone: 'info', result: 'recup' },
            { key: 'J', label: 'Absence justifiée', desc: 'retirée de la paie (1/22)', tone: 'ok', result: 'aj' },
            { key: 'V', label: 'Valider 8h00', desc: 'journée standard reconstituée', tone: 'ok', result: 'p' },
        ],
    },
    double: {
        title: 'Anomalie de pointage',
        options: [
            { key: 'V', label: 'Valider 8h00', desc: 'journée standard reconstituée', tone: 'ok', result: 'p' },
            { key: 'S', label: 'Saisir l’horaire', desc: 'corriger entrée / sortie', tone: 'shf', manual: true },
            { key: 'R', label: 'Récupération', desc: 'journée en récup', tone: 'info', result: 'recup' },
            { key: 'M', label: 'Maladie', desc: 'sous réserve du justificatif', tone: 'info', result: 'm' },
            { key: 'C', label: 'Congé', desc: 'journée posée sur le solde', tone: 'shf', result: 'c' },
        ],
    },
};
// ── Custom decision types in the paie menu ───────────────────
// « Absence à requalifier » (absj) is the same human call as the daily
// arbitrage — so the types the team defined in Paramètres belong in that
// menu too, not only in Saisie quotidienne. They resolve to 'aj'
// (absence justifiée), mirroring how the daily flow stores them
// (decisionType AUTRE → cell type 'aj'). The other two kinds are hour
// corrections, not requalifications, so they keep their fixed menu.
export const GM_CUSTOM_KINDS = ['absj'];
/** One catalogue type rendered as a paie option. The stored key IS the
 * code, so a decision keeps pointing at the same type after a rename.
 * « Absence injustifiée » keeps its own meaning (anj, 0 h) — every other
 * custom type counts the day justified, like the daily flow's AUTRE. */
export function gmCustomOption(t) {
    if (t.code === ABSENCE_INJUSTIFIEE_CODE) {
        return {
            key: t.code,
            label: t.label,
            desc: 'sans solde · 0 h en paie',
            tone: 'warn',
            result: 'anj',
            customCode: t.code,
        };
    }
    return {
        key: t.code,
        label: t.label,
        desc: 'type personnalisé · retiré de la paie (1/22)',
        tone: 'info',
        result: 'aj',
        customCode: t.code,
    };
}
/** The menu for a kind, with the active custom types appended where they
 * apply. `customs` may be empty/undefined — the built-in menu is the
 * floor, never replaced. */
export function gmMenuFor(kind, customs) {
    const base = GM_KIND_MENUS[kind];
    if (!GM_CUSTOM_KINDS.includes(kind) || !customs || customs.length === 0)
        return base;
    return { title: base.title, options: [...base.options, ...customs.map(gmCustomOption)] };
}
/** Look up a storable (non-manual) option; null when unknown or manual-only.
 * `customs` lets a catalogue type resolve — the server passes the active
 * ones so a client menu entry and its validation agree. */
export function gmFindStorableOption(kind, key, customs) {
    const opt = gmMenuFor(kind, customs).options.find((o) => o.key === key);
    return opt && !opt.manual && opt.result ? opt : null;
}
//# sourceMappingURL=grille.js.map